import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  ChevronRight,
  Navigation,
  Phone,
  Route as RouteIcon,
  Check,
  Layers,
  EyeOff,
  Lightbulb,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  STATUS_META,
  STATUSES,
  ENTITY_META,
  VISIT_DEFAULT,
  VISIT_FILTERS,
  VISIT_ORDER,
  SCATTER_MIN,
  VISIT_LABEL,
} from "@/lib/constants";
import { groupByArea, mapSearchUrl, mapRouteUrl } from "@/lib/address";

/* ═══════════════════════ veri katmanı ═══════════════════════ */

/** Bir üye listesinin durum sayımını çıkarır; `hedef` aktif hedef kümesi. */
function say(uyeler, hedef) {
  const c = { toplam: uyeler.length, hedef: 0 };
  for (const s of STATUSES) c[s] = 0;
  for (const u of uyeler) {
    if (c[u.status] !== undefined) c[u.status] += 1;
    if (hedef.has(u.status)) c.hedef += 1;
  }
  return c;
}

/**
 * Kategori bloklarına ayırarak sıralar: VISIT_ORDER sırası, blok içinde ada
 * göre. Aynı kategoriden üyeler hep yan yana durur.
 * `sokakOnce` açıksa (dağınık kutusu) kategori içinde sokak adı da hesaba girer.
 */
function sirala(uyeler, sokakOnce = false) {
  const yer = (s) => {
    const i = VISIT_ORDER.indexOf(s);
    return i < 0 ? VISIT_ORDER.length : i;
  };
  return [...uyeler].sort((a, b) => {
    const fark = yer(a.status) - yer(b.status);
    if (fark !== 0) return fark;
    if (sokakOnce) {
      const s = (a._sokak || "").localeCompare(b._sokak || "", "tr");
      if (s !== 0) return s;
    }
    return (a.name || "").localeCompare(b.name || "", "tr");
  });
}

/**
 * Mahalle → sokak ağacını aktif hedef kümesine göre yeniden kurar.
 *
 * Üç iş yapar:
 *  1. Hedefi olmayan sokakları `gizliSokaklar`a ayırır (ekranı bunlar şişiriyor).
 *  2. SCATTER_MIN'den az üyeli sokakları mahallenin "Dağınık adresler"
 *     kutusuna toplar; sokak adı üye satırında etiket olarak yaşamaya devam eder.
 *  3. Her sokağın üyelerini hedef / hedef değil diye iki katmana böler.
 */
function agacKur(members, hedef) {
  const katmanla = (uyeler, sokakOnce) => {
    const birincil = [];
    const ikincil = [];
    for (const u of uyeler) (hedef.has(u.status) ? birincil : ikincil).push(u);
    return {
      birincil: sirala(birincil, sokakOnce),
      ikincil: sirala(ikincil, sokakOnce),
    };
  };

  return groupByArea(members).map((mahalle) => {
    const gorunen = [];
    const gizliSokaklar = [];
    const dagilan = [];

    for (const sokak of mahalle.sokaklar) {
      const hedefSayisi = sokak.üyeler.filter((u) => hedef.has(u.status)).length;
      if (hedefSayisi === 0) {
        gizliSokaklar.push({ ...sokak, ...katmanla(sokak.üyeler, false), dagilmis: false });
      } else if (sokak.üyeler.length < SCATTER_MIN) {
        dagilan.push(sokak);
      } else {
        gorunen.push({ ...sokak, ...katmanla(sokak.üyeler, false), dagilmis: false });
      }
    }

    if (dagilan.length > 0) {
      const uyeler = dagilan.flatMap((s) => s.üyeler.map((u) => ({ ...u, _sokak: s.ad })));
      gorunen.push({
        ad: "Dağınık adresler",
        anahtar: "__daginik",
        dagilmis: true,
        sokakSayisi: dagilan.length,
        üyeler: uyeler,
        ...katmanla(uyeler, true),
      });
    }

    const hedefSayisi = mahalle.üyeler.filter((u) => hedef.has(u.status)).length;
    return { ...mahalle, sokaklar: gorunen, gizliSokaklar, hedefSayisi };
  });
}

/* ═══════════════════════ küçük parçalar ═══════════════════════ */

/**
 * "Belirsiz"in rengi (#E5E7EB) beyaz zeminde görünmüyor. Eskiden sorun değildi,
 * çünkü belirsizler kalabalığın içinde kayboluyordu; artık ana hedef oldukları
 * için sokakta hangisinin askıda hangisinin belirsiz olduğu bir bakışta
 * okunmalı. Rengi değiştirmek yerine (o renk kanban ve haritada da kullanılıyor)
 * sadece rota ekranında ince bir çerçeve veriyoruz.
 */
const SOLUK_DURUMLAR = new Set(["Beyaz"]);
const cerceve = (durum) =>
  SOLUK_DURUMLAR.has(durum) ? "1px solid #9CA3AF" : "none";

/**
 * Durum dağılımını tek şeritte gösterir.
 * Hedef kümesindekiler tam parlaklıkta, gerisi soluk: şeride bakınca "burada
 * ne kadar iş var" hemen okunsun.
 */
function DurumSeridi({ sayim, hedef }) {
  const toplam = sayim.toplam || 1;
  return (
    <div className="flex h-2 w-full rounded-full overflow-hidden bg-neutral-100 dark:bg-neutral-800">
      {STATUSES.map((s) => {
        const adet = sayim[s] || 0;
        if (adet === 0) return null;
        return (
          <div
            key={s}
            title={`${STATUS_META[s].label}: ${adet}`}
            style={{
              width: `${(adet / toplam) * 100}%`,
              background: STATUS_META[s].dot,
              opacity: hedef.has(s) ? 1 : 0.3,
            }}
          />
        );
      })}
    </div>
  );
}

/** Durum rozetleri; hedef olanlar belirgin, gerisi soluk. */
function DurumRozetleri({ sayim, hedef, kompakt = false }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
      {STATUSES.map((s) => {
        const adet = sayim[s] || 0;
        if (adet === 0) return null;
        const meta = STATUS_META[s];
        const vurgu = hedef.has(s);
        return (
          <span
            key={s}
            className={`inline-flex items-center gap-1 ${kompakt ? "text-[9.5px]" : "text-[10.5px]"} ${
              vurgu
                ? "font-semibold text-neutral-800 dark:text-neutral-100"
                : "font-normal text-neutral-400 dark:text-neutral-500"
            }`}
          >
            <span
              className="rounded-full shrink-0"
              style={{
                background: meta.dot,
                opacity: vurgu ? 1 : 0.45,
                height: vurgu ? 8 : 6,
                width: vurgu ? 8 : 6,
                border: vurgu ? cerceve(s) : "none",
              }}
            />
            <span className="font-mono">{adet}</span>
            <span className="uppercase tracking-[0.05em]">{meta.short}</span>
          </span>
        );
      })}
    </div>
  );
}

function UyeSatiri({ uye, vurgu, onOpenDetail }) {
  const meta = STATUS_META[uye.status] || STATUS_META.Beyaz;
  const em = ENTITY_META[uye.entity_type] || ENTITY_META.Firma;
  const telefon = (uye.phone || "").split("\n")[0]?.split(" - ")[0]?.replace(/[^\d+]/g, "");

  return (
    <div
      className={`flex items-center gap-2.5 rounded-lg border px-2.5 py-2 transition-colors ${
        vurgu
          ? "border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900"
          : "border-neutral-100 dark:border-neutral-800 bg-neutral-50/60 dark:bg-neutral-900/40"
      }`}
      data-testid={`route-member-${uye.id}`}
    >
      <span
        className="h-7 w-1.5 rounded-full shrink-0"
        style={{
          background: meta.dot,
          opacity: vurgu ? 1 : 0.4,
          border: vurgu ? cerceve(uye.status) : "none",
        }}
      />
      <button
        type="button"
        onClick={() => onOpenDetail?.(uye)}
        className="min-w-0 flex-1 text-left group"
      >
        <div
          className={`text-[12.5px] leading-tight truncate group-hover:underline ${
            vurgu
              ? "font-semibold text-neutral-950 dark:text-neutral-50"
              : "font-normal text-neutral-500 dark:text-neutral-400"
          }`}
        >
          {uye.name}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
          <span
            className={`text-[9px] uppercase tracking-wider font-semibold px-1 py-px rounded shrink-0 ${em.bg} ${em.text}`}
            title={em.vote}
          >
            {em.short}
          </span>
          {/* Dağınık adresler kutusunda sokak adı burada yaşar. */}
          {uye._sokak && (
            <span className="text-[9.5px] font-mono text-neutral-400 dark:text-neutral-500 truncate shrink-0">
              {uye._sokak}
            </span>
          )}
          {uye.company && uye.company !== uye.name && (
            <span className="text-[10px] text-neutral-400 dark:text-neutral-500 truncate">
              {uye.company}
            </span>
          )}
        </div>
      </button>
      <div className="flex items-center gap-0.5 shrink-0">
        {telefon && (
          <a
            href={`tel:${telefon}`}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-neutral-500 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800"
            aria-label={`${uye.name} — ara`}
          >
            <Phone className="h-3.5 w-3.5" />
          </a>
        )}
        <a
          href={mapSearchUrl(uye.address)}
          target="_blank"
          rel="noreferrer"
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-neutral-500 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label={`${uye.name} — yol tarifi`}
          data-testid={`route-directions-${uye.id}`}
        >
          <Navigation className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
}

/**
 * Çaya gitmeye gerek olmayan üyeler — kapalı açılan ikincil katman.
 * Gizlemek yerine katlıyoruz: yer kaplamıyor ama veri de kaybolmuyor.
 */
function IkincilKutu({ uyeler, onOpenDetail, testId }) {
  const [acik, setAcik] = useState(false);
  if (uyeler.length === 0) return null;

  return (
    <div className="rounded-lg border border-dashed border-neutral-200 dark:border-neutral-800">
      <button
        type="button"
        onClick={() => setAcik((v) => !v)}
        className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-neutral-100/60 dark:hover:bg-neutral-800/40 rounded-lg transition-colors"
        data-testid={testId}
      >
        <ChevronRight
          className={`h-3 w-3 shrink-0 text-neutral-400 transition-transform ${acik ? "rotate-90" : ""}`}
        />
        <EyeOff className="h-3 w-3 shrink-0 text-neutral-400" />
        <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
          Çaya gitmeye gerek olmayan{" "}
          <span className="font-mono font-semibold text-neutral-600 dark:text-neutral-300">
            {uyeler.length}
          </span>{" "}
          üye
        </span>
      </button>
      <AnimatePresence initial={false}>
        {acik && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="p-2 pt-0 space-y-1.5">
              {uyeler.map((u) => (
                <UyeSatiri key={u.id} uye={u} vurgu={false} onOpenDetail={onOpenDetail} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════ sokak ve mahalle ═══════════════════════ */

/** Gerçek sokak ya da "Dağınık adresler" kutusu — ikisi de aynı satır. */
function Sokak({ sokak, hedef, onOpenDetail }) {
  const [acik, setAcik] = useState(false);
  const sayim = useMemo(() => say(sokak.üyeler, hedef), [sokak, hedef]);
  const rotaUrl = useMemo(
    () => mapRouteUrl(sokak.birincil.map((u) => u.address)),
    [sokak]
  );
  // Hedefi olmayan (elle açılmış) sokakta ikinci katman anlamsız: hepsi tek liste.
  const hedefsiz = sokak.birincil.length === 0;
  const govde = hedefsiz ? sokak.ikincil : sokak.birincil;

  return (
    <div
      className={`rounded-lg border overflow-hidden ${
        sokak.dagilmis
          ? "border-dashed border-neutral-300 dark:border-neutral-700"
          : "border-neutral-200 dark:border-neutral-800"
      }`}
    >
      <button
        type="button"
        onClick={() => setAcik((v) => !v)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors text-left"
        data-testid={`route-street-${sokak.anahtar || sokak.ad}`}
      >
        <ChevronRight
          className={`h-3.5 w-3.5 shrink-0 text-neutral-400 transition-transform ${acik ? "rotate-90" : ""}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            {sokak.dagilmis && <Layers className="h-3 w-3 shrink-0 text-neutral-400" />}
            <div className="text-[12.5px] font-medium text-neutral-900 dark:text-neutral-100 truncate">
              {sokak.ad}
            </div>
            {sokak.dagilmis && (
              <span className="text-[9.5px] font-mono text-neutral-400 shrink-0">
                {sokak.sokakSayisi} sokak
              </span>
            )}
          </div>
          <div className="mt-1">
            <DurumRozetleri sayim={sayim} hedef={hedef} kompakt />
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading text-base font-semibold font-mono text-neutral-900 dark:text-neutral-100 leading-none">
            {sayim.hedef}
          </div>
          <div className="text-[8.5px] uppercase tracking-wider text-neutral-400">çaya gider</div>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {acik && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="overflow-hidden border-t border-neutral-100 dark:border-neutral-800"
          >
            <div className="p-2.5 space-y-1.5 bg-neutral-50/50 dark:bg-neutral-950/30">
              {rotaUrl && sokak.birincil.length > 1 && (
                <a href={rotaUrl} target="_blank" rel="noreferrer" className="block">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full h-9 gap-1.5 text-xs"
                    data-testid={`route-street-directions-${sokak.anahtar || sokak.ad}`}
                  >
                    <RouteIcon className="h-3.5 w-3.5" />
                    {sokak.dagilmis ? "Bu mahalledeki dağınık" : "Bu sokakta çaya gidilecek"}{" "}
                    {Math.min(sokak.birincil.length, 10)} adres için rota oluştur
                  </Button>
                </a>
              )}
              {govde.map((u) => (
                <UyeSatiri
                  key={u.id}
                  uye={u}
                  vurgu={!hedefsiz}
                  onOpenDetail={onOpenDetail}
                />
              ))}
              {!hedefsiz && (
                <IkincilKutu
                  uyeler={sokak.ikincil}
                  onOpenDetail={onOpenDetail}
                  testId={`route-street-secondary-${sokak.anahtar || sokak.ad}`}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Tum mahalleler kapali acilir; kullanici istedigini kendisi acar.
function Mahalle({ mahalle, hedef, onOpenDetail }) {
  const [acik, setAcik] = useState(false);
  const [gizliAcik, setGizliAcik] = useState(false);
  const sayim = useMemo(() => say(mahalle.üyeler, hedef), [mahalle, hedef]);

  return (
    <motion.div
      layout
      className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden"
      data-testid={`route-area-${mahalle.ad}`}
    >
      <button
        type="button"
        onClick={() => setAcik((v) => !v)}
        className="w-full flex items-center gap-2 sm:gap-3 px-3.5 sm:px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors text-left"
      >
        <span className="h-9 w-9 shrink-0 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
          <MapPin className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
        </span>
        <div className="min-w-0 flex-1">
          {/* Mobilde ad kendi satırında: "232 üye · 86 sokak" shrink-0 olduğu için
              dar ekranda mahalle adını "M…"ye kadar eziyordu. */}
          <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2 min-w-0">
            <h3 className="font-heading font-semibold text-[15px] text-neutral-950 dark:text-neutral-50 break-words line-clamp-2 sm:line-clamp-none sm:truncate">
              {mahalle.ad}
            </h3>
            <span className="text-[10px] font-mono text-neutral-400 shrink-0">
              {mahalle.üyeler.length} üye · {mahalle.sokaklar.length} liste
            </span>
          </div>
          <div className="mt-1.5">
            <DurumSeridi sayim={sayim} hedef={hedef} />
          </div>
          <div className="mt-1.5">
            <DurumRozetleri sayim={sayim} hedef={hedef} />
          </div>
        </div>
        <div className="text-right shrink-0 pl-1">
          <div className="font-heading text-2xl font-light tracking-tighter text-neutral-950 dark:text-neutral-50 leading-none">
            {sayim.hedef}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-neutral-400 mt-0.5">
            {VISIT_LABEL}
          </div>
          <div className="text-[9px] font-mono text-neutral-300 dark:text-neutral-600">
            / {sayim.toplam}
          </div>
        </div>
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform ${acik ? "rotate-90" : ""}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {acik && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-t border-neutral-100 dark:border-neutral-800"
          >
            <div className="p-2.5 sm:p-3 space-y-2 bg-neutral-50/60 dark:bg-neutral-950/40">
              {mahalle.sokaklar.map((s) => (
                <Sokak
                  key={s.anahtar || s.ad}
                  sokak={s}
                  hedef={hedef}
                  onOpenDetail={onOpenDetail}
                />
              ))}

              {/* Gidilecek üyesi olmayan sokaklar: gizli ama silinmiş değil. */}
              {mahalle.gizliSokaklar.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setGizliAcik((v) => !v)}
                    className="w-full flex items-center justify-center gap-1.5 py-2 text-[11px] text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 transition-colors"
                    data-testid={`route-area-hidden-${mahalle.ad}`}
                  >
                    <EyeOff className="h-3 w-3" />
                    {gizliAcik ? "Gizle" : "Gidilecek üye olmayan"}{" "}
                    <span className="font-mono font-semibold">
                      {mahalle.gizliSokaklar.length}
                    </span>{" "}
                    {gizliAcik ? "" : "sokağı göster"}
                  </button>
                  {gizliAcik &&
                    mahalle.gizliSokaklar.map((s) => (
                      <Sokak
                        key={s.ad}
                        sokak={s}
                        hedef={hedef}
                        onOpenDetail={onOpenDetail}
                      />
                    ))}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ═══════════════════════ süzgeç düğmesi ═══════════════════════ */

/**
 * Aç/kapa süzgeç düğmesi. Açıkken tik işareti çıkıyor: kullanıcı düğmenin
 * basılı durduğunu ve tekrar basınca çıkacağını görsün.
 */
function HedefDugmesi({ durum, aktif, adet, onToggle }) {
  const meta = STATUS_META[durum];
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onToggle}
      aria-pressed={aktif}
      className={`h-9 gap-1.5 px-2.5 transition-colors ${
        aktif
          ? "bg-neutral-950 text-white border-neutral-950 hover:bg-neutral-800 hover:text-white dark:bg-white dark:text-neutral-950 dark:border-white dark:hover:bg-neutral-200"
          : "text-neutral-600 dark:text-neutral-300"
      }`}
      data-testid={`route-filter-${meta.key}`}
    >
      {aktif ? (
        <Check className="h-3.5 w-3.5" />
      ) : (
        <span
          className="h-2.5 w-2.5 rounded-full shrink-0"
          style={{ background: meta.dot, border: cerceve(durum) }}
        />
      )}
      <span className="text-xs">{meta.short}</span>
      <span className={`text-[10px] font-mono ${aktif ? "opacity-70" : "text-neutral-400"}`}>
        {adet}
      </span>
    </Button>
  );
}

/* ═══════════════════════ bilgi ve ipucu ═══════════════════════ */

/**
 * Ekranın nasıl okunacağını anlatan metin. Mobilde yer kaplamasın diye ilk
 * cümlesi açıkta, gerisi "Daha fazla" ile açılıyor.
 */
function BilgiKutusu() {
  const [acik, setAcik] = useState(false);

  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white/60 dark:bg-neutral-900/60 px-3.5 py-2.5">
      <p className="text-[11.5px] leading-relaxed text-neutral-600 dark:text-neutral-400">
        <span className="font-semibold text-neutral-800 dark:text-neutral-200">
          Mahallelere göre çaya gidilecek üyeler.
        </span>{" "}
        Yukarıdaki düğmeler kimin peşine düşüleceğini belirler; ekrandaki bütün
        sayılar ve rotalar ona göre değişir.
        {!acik && (
          <>
            {" "}
            <button
              type="button"
              onClick={() => setAcik(true)}
              className="font-semibold text-neutral-700 dark:text-neutral-300 underline underline-offset-2 hover:text-neutral-950 dark:hover:text-white"
              data-testid="route-info-more"
            >
              Daha fazla
            </button>
          </>
        )}
      </p>

      {/* Bilerek animasyonsuz: uc satirlik bir paragraf icin acilir yukseklik
          animasyonu, tamamlanmadigi durumda son satiri overflow-hidden altinda
          birakiyordu. Basit kosullu cizim hem kisa hem guvenli. */}
      {acik && (
          <p className="text-[11.5px] leading-relaxed text-neutral-600 dark:text-neutral-400 pt-1.5">
            Karşı taraftakiler ve yetki belgesi alınmış üyeler{" "}
            <strong>listeyi şişirmesin diye</strong> sokağın altındaki kapalı
            kutuya alınır — kaybolmazlar, açıp bakabilirsiniz.{" "}
            <strong>Dağınık adresler</strong> kutusu, o mahallede {SCATTER_MIN}
            &apos;ten az üyesi olan sokakları bir arada toplar; sokak adı üyenin
            yanında yazar. Gidilecek üyesi kalmayan sokak ve mahalleler listenin
            dibinde gizlidir.{" "}
            <button
              type="button"
              onClick={() => setAcik(false)}
              className="font-semibold text-neutral-700 dark:text-neutral-300 underline underline-offset-2 hover:text-neutral-950 dark:hover:text-white"
              data-testid="route-info-less"
            >
              Daha az
            </button>
          </p>
      )}
    </div>
  );
}

/**
 * Süzgecin o anki hâline göre değişen tavsiye. Sabit bir metin bir süre sonra
 * okunmaz oluyor; ipucu ekranın durumuna cevap verdiğinde işe yarıyor.
 */
function ipucuMetni(secili, suzgecKapali) {
  if (suzgecKapali) {
    return (
      <>
        Süzgeç kapalı, herkes listeleniyor. Sahaya çıkarken{" "}
        <strong>Askıda</strong> ve <strong>Belirsiz</strong>&apos;i açık bırakmak
        listeyi yalnızca gidilecek üyelere indirir.
      </>
    );
  }
  if (secili.has("Onay")) {
    return (
      <>
        Onay/söz alınmış üyeler şimdi listede. Onlara gitme sebebiniz ikna değil,{" "}
        <strong>yetki belgesi toplamak</strong>: şirketler ancak yetki belgesiyle
        oy kullanabiliyor, şahıslar kimliğiyle kullanıyor. Üyenin yanındaki{" "}
        <em>Şirket</em> / <em>Şahıs</em> etiketi hangisi olduğunu söyler.
      </>
    );
  }
  return (
    <>
      Onay/söz almış olmanız yeterli değil — şirket üyeler sandıkta ancak{" "}
      <strong>yetki belgesiyle</strong> oy kullanabiliyor. <strong>Onay</strong>{" "}
      süzgecini açarak yetki belgesi toplamak için yola koyulabilirsiniz.
    </>
  );
}

function IpucuKutusu({ secili, suzgecKapali }) {
  return (
    <div
      className="flex items-start gap-2.5 rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50/70 dark:bg-amber-950/25 px-3.5 py-2.5"
      data-testid="route-tip"
    >
      <Lightbulb className="h-4 w-4 shrink-0 mt-px text-amber-600 dark:text-amber-500" />
      <div className="min-w-0">
        <div className="text-[9.5px] uppercase tracking-[0.12em] font-semibold text-amber-700 dark:text-amber-500">
          İpucu
        </div>
        <p className="text-[11.5px] leading-relaxed text-amber-900/90 dark:text-amber-200/80 mt-0.5">
          {ipucuMetni(secili, suzgecKapali)}
        </p>
      </div>
    </div>
  );
}

/* ═══════════════════════ panel ═══════════════════════ */

export default function RoutePanel({ members, onOpenDetail }) {
  const [secili, setSecili] = useState(() => new Set(VISIT_DEFAULT));
  const [gizliMahalleAcik, setGizliMahalleAcik] = useState(false);

  // Hiçbir düğme basılı değilse süzgeç yok sayılır: herkes hedef olur.
  const suzgecKapali = secili.size === 0;
  const hedef = useMemo(
    () => (suzgecKapali ? new Set(STATUSES) : secili),
    [secili, suzgecKapali]
  );

  const agac = useMemo(() => agacKur(members, hedef), [members, hedef]);
  const gorunen = useMemo(() => agac.filter((m) => m.hedefSayisi > 0), [agac]);
  const gizliMahalleler = useMemo(() => agac.filter((m) => m.hedefSayisi === 0), [agac]);

  const durumSayilari = useMemo(() => {
    const c = {};
    for (const s of STATUSES) c[s] = 0;
    for (const m of members) if (c[m.status] !== undefined) c[m.status] += 1;
    return c;
  }, [members]);

  const toplamHedef = useMemo(
    () => members.filter((m) => hedef.has(m.status)).length,
    [members, hedef]
  );
  const toplamListe = useMemo(
    () => gorunen.reduce((a, m) => a + m.sokaklar.length, 0),
    [gorunen]
  );
  const gizliSokakSayisi = useMemo(
    () => agac.reduce((a, m) => a + m.gizliSokaklar.length, 0),
    [agac]
  );

  const degistir = (durum) =>
    setSecili((onceki) => {
      const yeni = new Set(onceki);
      if (yeni.has(durum)) yeni.delete(durum);
      else yeni.add(durum);
      return yeni;
    });

  if (members.length === 0) {
    return (
      <div className="bg-white dark:bg-neutral-900 border border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg p-8 text-center">
        <div className="text-sm text-neutral-500 dark:text-neutral-400">
          Gösterilecek üye yok.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="route-panel">
      {/* Özet */}
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl px-4 py-3 space-y-3">
        <div className="flex items-center gap-4 sm:gap-6">
          <div>
            <div className="font-heading text-xl font-semibold font-mono text-neutral-950 dark:text-neutral-50 leading-none">
              {gorunen.length}
            </div>
            <div className="text-[9.5px] uppercase tracking-[0.1em] text-neutral-500 dark:text-neutral-400 mt-1">
              Mahalle
            </div>
          </div>
          <div>
            <div className="font-heading text-xl font-semibold font-mono text-neutral-950 dark:text-neutral-50 leading-none">
              {toplamListe}
            </div>
            <div className="text-[9.5px] uppercase tracking-[0.1em] text-neutral-500 dark:text-neutral-400 mt-1">
              Sokak listesi
            </div>
          </div>
          <div>
            <div className="font-heading text-xl font-semibold font-mono text-neutral-950 dark:text-neutral-50 leading-none">
              {toplamHedef}
            </div>
            <div className="text-[9.5px] uppercase tracking-[0.1em] text-neutral-500 dark:text-neutral-400 mt-1">
              Çaya gidilecek
            </div>
          </div>
        </div>

        {/* Süzgeç: hedef kümesini bu düğmeler belirler */}
        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
          {/* Dar ekranda bu etiket üçüncü düğmeyi alt satıra itiyordu. */}
          <span className="hidden sm:inline text-[10px] uppercase tracking-[0.1em] text-neutral-400 mr-0.5">
            Göster
          </span>
          {VISIT_FILTERS.map((s) => (
            <HedefDugmesi
              key={s}
              durum={s}
              aktif={secili.has(s)}
              adet={durumSayilari[s] || 0}
              onToggle={() => degistir(s)}
            />
          ))}
          {suzgecKapali && (
            <span className="text-[10.5px] text-neutral-500 dark:text-neutral-400 ml-1">
              Süzgeç kapalı — herkes listeleniyor
            </span>
          )}
        </div>
      </div>

      <BilgiKutusu />
      <IpucuKutusu secili={secili} suzgecKapali={suzgecKapali} />

      {gorunen.map((m) => (
        <Mahalle key={m.ad} mahalle={m} hedef={hedef} onOpenDetail={onOpenDetail} />
      ))}

      {gorunen.length === 0 && (
        <div className="bg-white dark:bg-neutral-900 border border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg p-8 text-center">
          <div className="text-sm text-neutral-500 dark:text-neutral-400">
            Seçilen süzgeçle gidilecek üye çıkmadı.
          </div>
        </div>
      )}

      {/* Tamamen boşalan mahalleler */}
      {gizliMahalleler.length > 0 && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setGizliMahalleAcik((v) => !v)}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-dashed border-neutral-300 dark:border-neutral-700 text-[11.5px] text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 hover:bg-white/60 dark:hover:bg-neutral-900/60 transition-colors"
            data-testid="route-hidden-areas"
          >
            <EyeOff className="h-3.5 w-3.5" />
            {gizliMahalleAcik ? (
              "Boş mahalleleri gizle"
            ) : (
              <>
                Gidilecek üye olmayan{" "}
                <span className="font-mono font-semibold">{gizliMahalleler.length}</span>{" "}
                mahalleyi göster
                {gizliSokakSayisi > 0 && (
                  <span className="text-neutral-400">
                    {" "}
                    · toplam {gizliSokakSayisi} boş sokak
                  </span>
                )}
              </>
            )}
          </button>
          {gizliMahalleAcik &&
            gizliMahalleler.map((m) => (
              <Mahalle key={m.ad} mahalle={m} hedef={hedef} onOpenDetail={onOpenDetail} />
            ))}
        </div>
      )}
    </div>
  );
}
